// AiGovernanceRatificationCard — post-intake AI-governance signoff
// (2026-05-28). Pre-Export Review surface for the case where the
// Smart Intake wizard didn't surface the AI-governance block at
// intake time (the architect picked a non-ML/AI shape) but the
// generated SR/BR text declares an AI-derived output, and
// `PEG-AI-GOVERNANCE-SIGNATURE-001` is now blocking Phase 5.
//
// UX mirrors the wizard's Step 3 block:
//   * Two disposition buttons — ``in_scope`` / ``not_applicable``.
//   * Required ratifier name; optional rationale.
//   * On submit, PATCH /api/projects/{id}/intake/ai-governance —
//     the endpoint persists both intake.ai_governance_* and the
//     per-run phase1/ai-governance-decision.json that the gate
//     reads on the next evaluation.
//
// The card surfaces its own already-ratified state on mount so a
// user returning to the surface doesn't get nudged to re-ratify
// what's already signed.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { api, ApiError } from '@/lib/api'
import { toast } from '@/hooks/use-toast'

interface AiGovernanceRatificationCardProps {
  projectId: string
}

type Disposition = 'in_scope' | 'not_applicable' | null

export function AiGovernanceRatificationCard({
  projectId,
}: AiGovernanceRatificationCardProps) {
  const queryClient = useQueryClient()
  const context = useQuery({
    queryKey: ['context', projectId],
    queryFn: () => api.getIntake(projectId),
  })

  const intake = context.data?.intake
  const persistedDisposition =
    (intake?.ai_governance_disposition ?? null) as Disposition
  const persistedRatifier = intake?.ai_governance_ratified_by ?? ''
  const persistedRationale = intake?.ai_governance_rationale ?? ''

  const [disposition, setDisposition] = useState<Disposition>(persistedDisposition)
  const [ratifier, setRatifier] = useState(persistedRatifier)
  const [rationale, setRationale] = useState(persistedRationale)
  const [error, setError] = useState<string | null>(null)

  // Hydrate state from the loaded context. We only set ONCE per
  // successful load — subsequent typing wins over a stale fetch.
  useEffect(() => {
    if (context.isSuccess) {
      setDisposition(persistedDisposition)
      setRatifier(persistedRatifier)
      setRationale(persistedRationale)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isSuccess])

  const mutate = useMutation({
    mutationFn: async () => {
      setError(null)
      if (!disposition) {
        throw new Error('Pick a disposition first.')
      }
      if (!ratifier.trim()) {
        throw new Error(
          'Ratifier name is required for both signed dispositions.',
        )
      }
      return api.patchAiGovernance(projectId, {
        disposition,
        ratified_by: ratifier.trim(),
        rationale: rationale.trim() || null,
      })
    },
    onSuccess: () => {
      toast({
        title: 'AI governance ratified',
        description:
          disposition === 'in_scope'
            ? 'Signed in-scope. The export gate will see the signature on the next evaluation.'
            : 'Signed not-applicable. The export gate will accept the disposition on the next evaluation.',
      })
      queryClient.invalidateQueries({ queryKey: ['context', projectId] })
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message)
    },
  })

  const isAlreadyRatified =
    persistedDisposition !== null && persistedRatifier.trim().length > 0

  return (
    <div className="alm-card p-5">
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck className="h-5 w-5 text-indigo-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">
            AI Governance Ratification
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Closes{' '}
            <code className="text-[10.5px]">
              PEG-AI-GOVERNANCE-SIGNATURE-001
            </code>{' '}
            when the project's BR/SR text declares an AI-derived output.
            Both options are <em>signed</em> dispositions — the
            architect must name themselves either way.
          </div>
        </div>
        {isAlreadyRatified && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
            <CheckCircle2 className="h-3 w-3" /> ratified
          </span>
        )}
      </div>

      {context.isLoading && (
        <div className="text-xs text-slate-500 flex items-center gap-2 py-3">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading intake…
        </div>
      )}

      {!context.isLoading && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => setDisposition('in_scope')}
              className={`text-left p-3 rounded-md border transition ${
                disposition === 'in_scope'
                  ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-200'
                  : 'border-slate-700 hover:bg-white/5 text-slate-300'
              }`}
            >
              <div className="text-sm font-semibold">In scope</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                We'll apply an AI governance program (NIST AI RMF,
                model card, bias / drift monitoring, …).
              </div>
            </button>
            <button
              type="button"
              onClick={() => setDisposition('not_applicable')}
              className={`text-left p-3 rounded-md border transition ${
                disposition === 'not_applicable'
                  ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-200'
                  : 'border-slate-700 hover:bg-white/5 text-slate-300'
              }`}
            >
              <div className="text-sm font-semibold">Not applicable</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                The AI element doesn't trigger governance obligations
                in this scope (architect affirms).
              </div>
            </button>
          </div>

          {disposition && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                  htmlFor="ai-gov-ratifier"
                >
                  Ratified by *
                </label>
                <input
                  id="ai-gov-ratifier"
                  type="text"
                  value={ratifier}
                  onChange={(e) => setRatifier(e.target.value)}
                  placeholder="e.g. Sam Lee, AI Governance Lead"
                  className={`w-full bg-slate-950/60 border rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60 ${
                    !ratifier.trim()
                      ? 'border-amber-700/50'
                      : 'border-slate-700'
                  }`}
                />
              </div>
              <div>
                <label
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                  htmlFor="ai-gov-rationale"
                >
                  Rationale{' '}
                  <span className="text-slate-600 normal-case tracking-normal">
                    (optional)
                  </span>
                </label>
                <input
                  id="ai-gov-rationale"
                  type="text"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder={
                    disposition === 'not_applicable'
                      ? 'Why governance does not apply here'
                      : 'Brief context (e.g. NIST AI RMF tier)'
                  }
                  className="w-full bg-slate-950/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-700/50 bg-red-950/40 p-2 text-[11px] text-red-200 mb-3 flex items-start gap-2">
              <X className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {!disposition && (
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mb-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400/80" />
              Pick a disposition above to enable submission.
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={
                mutate.isPending ||
                !disposition ||
                !ratifier.trim()
              }
              onClick={() => mutate.mutate()}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              {mutate.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {isAlreadyRatified ? 'Update ratification' : 'Sign + ratify'}
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
